// The rundown before a call (module 10 U354, U355, U365, U367, U368; pipeline RC5; brief of 7 Oct 2026, section 5).
//
// One component, three cuts, the same words in each (U352): the server shapes the rundown once
// (apps/api/src/copilot/rundown.ts) and this draws it in the room it has.
//   full   the Calendar brief: every section, every row, the cells of money and clocks, and what Cobrain could not see;
//   side   the meeting page's side column: each section folded to its first item, opened in place;
//   short  the co-pilot before the call: the stake, who is in the room, what is owed and what moved, as one sentence each.
// It also narrows by its own width (container queries in company.css), so a cut drawn in a narrow column stacks its cells
// and rows without the page saying so.
//
// Nothing here suggests a move for the call (module 13 L25; U367): the rundown states what is on record and stops. The
// drawing's two buttons act on what is owed or overdue; the actions they name are not built yet, so this draws none, and
// the record is one press away from every part.
import { ActionButton, CompanyLine, Lnk, PlateOf, type Go } from '../parts/index.js';
import { useState } from 'react';
import { stageTone } from '../deals/live-words.js';
import type { Lede, RundownData, RundownRow, RundownSection, StakeCell } from './api.js';
import './company.css';

export type Cut = 'full' | 'side' | 'short';

const BARS: Record<ReturnType<typeof stageTone>, number> = { talk: 1, send: 2, sent: 3, contract: 4, signed: 5 };

function LedeP({ lede, short = false }: { lede: Lede; short?: boolean }) {
  return <p className="rd-lede"><b>{lede.lead}</b>{!short && lede.rest ? ` ${lede.rest}` : null}</p>;
}

function Cell({ cell, go }: { cell: StakeCell; go: Go }) {
  return (
    <div className="rd-cell" data-cell={cell.key}>
      <span className="k">{cell.label}</span>
      <span className={cell.word ? 'fig word' : 'fig'}>{cell.figure}</span>
      {cell.split.length > 0
        ? <ul className="rd-split">{cell.split.map((s, i) => <li key={i}><span>{s.label}</span><b>{s.value}</b></li>)}</ul>
        : null}
      {cell.deals.map((d) => {
        const tone = stageTone(d.stageCode);
        return (
          <div className="rd-deal" key={d.id}>
            <Lnk to={`/deals/${d.id}`} go={go}>{d.title}</Lnk>
            <span className="fg">{d.figure ?? 'No figure'}</span>
            <span className="stg" data-stage={tone}>
              <span className="lv-track" aria-hidden="true">{[1, 2, 3, 4, 5].map((n) => <i key={n} className={n < BARS[tone] ? 'done' : n === BARS[tone] ? 'at' : undefined} />)}</span>
              <span className="stw">{d.stage}</span>
            </span>
          </div>
        );
      })}
      {cell.plate ? <span className="rd-dates"><PlateOf plate={cell.plate} />{cell.plateNote ? <span className="meta">{cell.plateNote}</span> : null}</span> : null}
      {cell.note ? <span className="meta">{cell.note}</span> : null}
    </div>
  );
}

function Row({ row, go }: { row: RundownRow; go: Go }) {
  switch (row.kind) {
    case 'dated':
      return <li><span className="d">{row.when}{row.label ? <span>{row.label}</span> : null}</span><span className="w">{row.text}</span></li>;
    case 'person':
      return (
        <li>
          <span className="who">
            <b>{row.name}</b>
            {row.company ? <Lnk className="co" to={`/companies/${row.company.id}`} go={go}>{row.company.name}</Lnk> : null}
            {row.status ? <span className="st">{row.status}</span> : null}
          </span>
          <span className="w">{row.text}</span>
        </li>
      );
    case 'owed':
      return (
        <li>
          <span className="hd2"><span className={row.side === 'ours' ? 'ours' : 'theirs'}>{row.who}</span></span>
          <span className="w">{row.text}</span>
          {row.quote ? <span className="qt">“{row.quote}”</span> : null}
          {row.meta ? <span className="meta">{row.meta}</span> : null}
          {row.notOnRecord ? <span className="st">Not on record yet</span> : null}
        </li>
      );
  }
}

const rowsClass = (s: RundownSection) => (s.key === 'people' ? 'rd-rows rd-ppl' : s.key === 'owed' ? 'rd-rows rd-owe' : 'rd-rows');

/** The co-pilot calls "Who is coming from their side" what it is beside a call. */
const shortTitle = (s: RundownSection) => (s.key === 'people' ? 'In the room' : s.key === 'owed' ? 'Owed' : s.title);

function Section({ s, cut, go }: { s: RundownSection; cut: Cut; go: Go }) {
  const [open, setOpen] = useState(cut === 'full');
  if (cut === 'short') {
    return <section className="rd-sec" data-sec={s.key}><h3>{shortTitle(s)}</h3><LedeP lede={s.key === 'moved' && s.rows[0] && s.rows[0].kind === 'dated' ? { lead: s.rows[0].text, rest: null } : s.lede} short /></section>;
  }
  // Folded, a section shows its lede, its first cell or row, and says how much more it holds.
  const cells = open ? s.cells : s.cells.slice(0, 1);
  const rows = open ? s.rows : s.rows.slice(0, 1);
  const hidden = s.cells.length - cells.length + (s.rows.length - rows.length) + (!open && (s.more || s.use) ? 1 : 0);
  return (
    <section className="rd-sec" data-sec={s.key}>
      <h3>{s.title}{s.sub ? <span className="sub">{s.sub}</span> : null}</h3>
      <LedeP lede={s.lede} />
      {cells.length > 0 ? <div className="rd-cells">{cells.map((c) => <Cell key={c.key} cell={c} go={go} />)}</div> : null}
      {rows.length > 0 ? <ul className={rowsClass(s)}>{rows.map((r, i) => <Row key={i} row={r} go={go} />)}</ul> : null}
      {open && s.more ? <p className="rd-more">{s.more}</p> : null}
      {open && s.use ? <><h4>Use</h4><LedeP lede={s.use} /></> : null}
      {!open && hidden > 0 ? <ActionButton reading type="button" className="fold2" aria-expanded="false" onClick={() => setOpen(true)}>Show {hidden} more</ActionButton> : null}
    </section>
  );
}

const SHORT_KEYS = new Set(['stake', 'people', 'owed', 'moved']);

export function Rundown({ data, cut = 'full', go, head }: { data: RundownData; cut?: Cut; go: Go; head?: React.ReactNode }) {
  const sections = cut === 'short' ? data.sections.filter((s) => SHORT_KEYS.has(s.key)) : data.sections;
  return (
    <div className={`rd rd-${cut}`}>
      <div className="rd-head">
        {head}
        <CompanyLine line={data.line} go={go} showName={cut !== 'short'} />
      </div>
      {sections.map((s) => <Section key={s.key} s={s} cut={cut} go={go} />)}
      <div className="rd-foot">
        {cut === 'full' && data.notSeen.length > 0
          ? <><h3>What Cobrain could not see</h3><ul>{data.notSeen.map((n, i) => <li key={i}>{n}</li>)}</ul></>
          : null}
        <Lnk className="go" to={`/companies/${data.line.id}`} go={go}>Open the {data.line.name} record</Lnk>
      </div>
    </div>
  );
}
